/**
 * Shared 1-Hz clock for live sandbox timers (expiry countdown + provisioning
 * elapsed). All subscribers tick in sync off a single `setInterval`, so any
 * number of timers on the page cost exactly one interval — see
 * SandboxExpiryCountdown and SandboxSetupPopover.
 */
import { useEffect, useState } from 'react';

const tickListeners = new Set<(now: number) => void>();
let tickInterval: number | null = null;

function subscribe(listener: (now: number) => void): () => void {
  tickListeners.add(listener);
  if (tickInterval === null) {
    tickInterval = window.setInterval(() => {
      const now = Date.now();
      for (const l of tickListeners) l(now);
    }, 1000);
  }
  return () => {
    tickListeners.delete(listener);
    if (tickListeners.size === 0 && tickInterval !== null) {
      window.clearInterval(tickInterval);
      tickInterval = null;
    }
  };
}

/** Subscribe to the shared 1-second tick; returns the current `Date.now()`. */
export function useTickingNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => subscribe(setNow), []);
  return now;
}

/**
 * Format a count-up elapsed duration as "Xm Ys" / "Ys". Clamped at zero so a
 * slightly skewed start timestamp never renders a negative timer.
 */
export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
}
